import { TabGroup } from '@headlessui/react';
import { Checkpoint } from '@langchain/langgraph-sdk';
import { ArrowLeftIcon, ArrowRightIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import * as React from 'react';

import LoadingSlimIcon from '@/Pages/Graph/icons/LoadingSlimIcon.svg?react';
import { ButtonWithTracking } from '@/components/ButtonWithTracking';
import { CodeData } from '@/components/Code/Code';
import { CopyInlineLink } from '@/components/CopyInlineLink';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import {
  Button,
  IconButton,
  TabLabel,
  TabList,
  TabPanel,
  TabPanels,
  Text,
  Tooltip,
} from '@/design-system';
import { cn } from '@/utils/tailwind';

import { useGraphThreadState } from '../../../api/useGraphSwr';
import {
  RenderModeContext,
  StateContext,
  useRenderModeCtx,
} from '../../../control/state';
import { isRunExecuting } from '../../../control/state.utils';
import {
  foreignDataToTree,
  parseForeignData,
} from '../../../data/foreign-data';
import { TraceLog } from '../../../data/misc';
import * as Tree from '../../../data/tree';
import { useReRunNode } from '../../../hooks/useReRunNode';
import { StudioFeatureFlagsContext } from '../../../hooks/useStudioFeatureFlagsContext';
import { useAssistantStore } from '../../../store/assistants/assistantsStore';
import { ForeignDataTreeView } from '../common/foreign-data-tree';
import { RtfAuto } from '../common/relative-time';

const TraceStateViewTabs = (props: {
  checkpoint?: Checkpoint | string;
  threadId: string;
}) => {
  const state = useGraphThreadState(props.threadId, props.checkpoint);
  const foreignData = React.useMemo(
    () => parseForeignData(state.data?.values, [], 'simple'),
    [state.data]
  );
  const renderModeState = useRenderModeCtx({ renderMode: 'pretty' });

  if (state.isLoading && !state.data) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  return (
    <RenderModeContext.Provider value={renderModeState}>
      <TabPanel className="flex flex-col gap-2 p-4">
        <ForeignDataTreeView
          data={foreignDataToTree({
            data: foreignData,
          })}
        />
      </TabPanel>
      <TabPanel>
        <CodeData maxHeight="30vh" value={state.data} readOnly />
      </TabPanel>
    </RenderModeContext.Provider>
  );
};

const TraceStateView = (props: { traceLog: TraceLog }) => {
  const state = React.useContext(StateContext);
  const [open, setOpen] = React.useState(false);
  if (!state.threadId) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="plain"
          size="sm"
          color="secondary"
          className="bg-transparent"
        >
          View state
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="max-h-[50vh] min-w-[600px] overflow-y-auto p-0"
        align="start"
      >
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-secondary bg-popover p-4 text-sm text-tertiary">
          Viewing checkpoint:{' '}
          <CopyInlineLink
            value={props.traceLog.meta.id}
            prefix="ID"
            className="font-mono"
          >
            {props.traceLog.meta.id}
          </CopyInlineLink>
        </div>
        <TabGroup>
          <TabList className="px-4 pt-4">
            <TabLabel label="Values" />
            <TabLabel label="JSON" />
          </TabList>
          <TabPanels>
            {open && (
              <TraceStateViewTabs
                threadId={state.threadId}
                checkpoint={
                  props.traceLog.state?.checkpoint ?? props.traceLog.meta.id
                }
              />
            )}
          </TabPanels>
        </TabGroup>
      </PopoverContent>
    </Popover>
  );
};

const TraceRetryButton = (props: {
  traceLog: TraceLog;
  onForkSuccess: () => void;
}) => {
  const state = React.useContext(StateContext);
  const config = useAssistantStore((state) => state.config);
  const updateTarget =
    props.traceLog.meta.source === 'input'
      ? null
      : props.traceLog.state?.checkpoint ?? props.traceLog.meta.id;

  const forkInputNull = useReRunNode({
    onSuccess: props.onForkSuccess,
    updateTarget,
  });

  if (!updateTarget || isRunExecuting(state.runState)) return null;
  return (
    <ButtonWithTracking
      as={Button}
      eventName="trace_retry"
      disabled={forkInputNull.isMutating}
      onClick={() => forkInputNull.trigger({ config })}
      size="sm"
      color="secondary"
      variant="plain"
      aria-label="Re-run from here"
      className="bg-transparent"
    >
      <Text variant="sm" weight="normal">
        Re-run from here
      </Text>
      {forkInputNull.isMutating && (
        <LoadingSlimIcon className="size-4 animate-spin" />
      )}
    </ButtonWithTracking>
  );
};

const isSamePath = (a: Tree.ForestPath, b: Tree.ForestPath) => {
  return a.length === b.length && a.every((x, i) => b[i] === x);
};

export const TraceSelectFork = (props: {
  name: string;
  paths: Array<Tree.ForestPath>;
  value: Tree.ForestPath;
  onChange: (value: Tree.ForestPath) => void;
  className?: string;
}) => {
  if (props.paths.length < 2) return null;
  const selectedIndex = props.paths.findIndex((path) =>
    isSamePath(props.value, path)
  );

  return (
    <div className={cn('flex items-center gap-2', props.className)}>
      <div className="flex items-center gap-1">
        <IconButton
          disabled={selectedIndex === 0}
          onClick={() => {
            props.onChange(props.paths[Math.max(0, selectedIndex - 1)]);
          }}
          data-testid="previous-fork-button"
          label="Previous fork"
          icon={ArrowLeftIcon}
          color="secondary"
          variant="plain"
          className="bg-transparent"
        />
        <IconButton
          disabled={selectedIndex === props.paths.length - 1}
          onClick={() => {
            props.onChange(
              props.paths[Math.min(props.paths.length - 1, selectedIndex + 1)]
            );
          }}
          data-testid="next-fork-button"
          label="Next fork"
          icon={ArrowRightIcon}
          color="secondary"
          variant="plain"
          className="bg-transparent"
        />
      </div>

      <span className="text-xs text-tertiary">
        Fork <span className="tabular-nums">{selectedIndex + 1}</span>
        {' of '}
        <span className="tabular-nums">{props.paths.length}</span>
      </span>
    </div>
  );
};

const CheckpointEntry = (props: {
  traceLog: TraceLog;
  children?: React.ReactNode;
  runIdsMap: Record<string, string>;
  isLast: boolean;
  className?: string;
  onForestPathChange: (value: Tree.ForestPath) => void;
  onForkSuccess: () => void;
}) => {
  const { threadId } = React.useContext(StateContext);
  const { enableMarkdownInStudio } = React.useContext(
    StudioFeatureFlagsContext
  );
  const renderDiamond = () => {
    return (
      <Tooltip title="Checkpoint" side="left">
        <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center bg-background">
          <div className="h-[7px] w-[7px] rotate-45 rounded-[1px] bg-current text-primary" />
        </div>
      </Tooltip>
    );
  };

  return (
    <div
      className={cn(
        'group',
        'items-center p-1.5',
        !enableMarkdownInStudio
          ? 'relative grid grid-cols-[auto,1fr] gap-x-3'
          : 'flex rounded-md hover:bg-primary-hover',
        props.className
      )}
      data-testid="checkpoint-entry"
    >
      {!enableMarkdownInStudio && (
        <div className="-mx-2 mr-1 flex flex-col items-center ">
          {renderDiamond()}
          <div
            className={cn(
              'relative flex flex-grow',
              props.isLast && 'opacity-0'
            )}
          >
            <span className="h-full w-px bg-tertiary" />
          </div>
        </div>
      )}

      <div className="flex w-full flex-col items-start gap-2">
        <div className="mr-4 flex w-full min-w-0 flex-row items-center gap-3 ">
          <TraceSelectFork
            value={props.traceLog.meta.path}
            onChange={(value) => props.onForestPathChange(value)}
            name={props.traceLog.entry.name}
            paths={props.traceLog.meta.forks}
          />
          <span className="size-1 rounded-full bg-[--black] first:hidden [&:not(:has(+*))]:hidden" />

          <Tooltip
            title={
              enableMarkdownInStudio && (
                <div className="flex items-center gap-2">
                  <Text variant="sm" weight="medium">
                    Checkpoint:
                  </Text>
                  <Text variant="sm">{props.traceLog.meta.id}</Text>
                </div>
              )
            }
            tooltipClassName="max-w-sm"
            side="left"
          >
            <span className="whitespace-nowrap text-xs text-tertiary">
              <RtfAuto time={new Date(props.traceLog.meta.timestamp)} />
            </span>
          </Tooltip>

          <div
            className={cn(
              'invisible flex items-center gap-3 group-hover:visible',
              enableMarkdownInStudio && 'ml-auto'
            )}
          >
            <span className="size-1 rounded-full bg-[--black] first:hidden [&:not(:has(+*))]:hidden" />

            {threadId && <TraceStateView traceLog={props.traceLog} />}

            {threadId && (
              <span className="size-1 rounded-full bg-[--black] first:hidden [&:not(:has(+*))]:hidden" />
            )}

            <TraceRetryButton
              traceLog={props.traceLog}
              onForkSuccess={props.onForkSuccess}
            />
          </div>
        </div>

        {props.children}
      </div>
    </div>
  );
};

export default CheckpointEntry;
